import type { Experiment } from 'types';

/**
 * Which experiments can meaningfully be combined.
 *
 * A variant combination only exists for traffic that was eligible for every
 * selected experiment at the same time. Two experiments that never ran
 * concurrently, or that targeted disjoint device types, share no such traffic,
 * any combination row for them would be an artefact.
 *
 * Overlap is partial, not exact: any shared day counts.
 */

export type DeviceType = 'All' | 'Desktop' | 'Mobile' | 'Tablet';

const CONCRETE_DEVICES: DeviceType[] = ['Desktop', 'Mobile', 'Tablet'];

/** Open-ended: a running experiment has no end date yet. */
const endOf = (experiment: Experiment): number => experiment.endsAt ?? Number.POSITIVE_INFINITY;
const startOf = (experiment: Experiment): number => experiment.startsAt ?? Number.NEGATIVE_INFINITY;

export const datesOverlap = (a: Experiment, b: Experiment): boolean => startOf(a) <= endOf(b) && startOf(b) <= endOf(a);

/**
 * No targeting rows, or an explicit 'All', means every device. Anything else is
 * the set of listed devices.
 */
export const devicesOf = (experiment: Experiment): DeviceType[] => {
  const rows = experiment.deviceTargeting ?? [];
  const devices = rows.map((r) => r.device).filter((d): d is DeviceType => !!d);
  if (devices.length === 0 || devices.includes('All')) return [...CONCRETE_DEVICES];
  return [...new Set(devices)];
};

export const devicesOverlap = (a: Experiment, b: Experiment): boolean => {
  const other = new Set(devicesOf(b));
  return devicesOf(a).some((d) => other.has(d));
};

/**
 * Whether two experiments could ever be shown on the same URL.
 *
 * Deliberately not part of incompatibleReason below. For the combination report,
 * targeting different pages is not a reason to refuse a pairing: one visitor can
 * see a product-page experiment and a checkout experiment in the same session, so
 * the combination is real. This is for the scheduler, which is asking a different
 * question — can these two collide — and for which a shared URL is the precondition
 * that makes a selector clash possible at all.
 *
 * The answer is deliberately asymmetric in its confidence. Proving two rule sets
 * *overlap* is impossible in general, so this proves the opposite where it can and
 * assumes overlap everywhere else. checkUrlTargeting matches with OR across rules
 * and treats no rules as "every URL", so two experiments are disjoint only when
 * every rule of one is provably disjoint from every rule of the other.
 */
export type UrlRule = { url: string; condition: string | null };

const normaliseUrl = (url: string): string => url.replace(/\/$/, '');

export const urlRulesOf = (experiment: Experiment): UrlRule[] =>
  (experiment.urlTargeting ?? []).map((row) => ({ url: normaliseUrl(row.url ?? ''), condition: row.condition }));

/**
 * True only when no URL can satisfy both rules.
 *
 * Only the positive, anchored conditions are decidable this way. 'contains' matches
 * an unbounded set, 'matches regex' would need to intersect two languages, and the
 * negations ('not equals', 'does not contain') each match almost everything, so any
 * pair involving them is reported as possibly-overlapping. Being wrong in that
 * direction costs a schedule that waits; being wrong the other way ships a broken
 * page.
 */
export const rulesProvablyDisjoint = (a: UrlRule, b: UrlRule): boolean => {
  const pair = `${a.condition}|${b.condition}`;

  switch (pair) {
    case 'equals|equals':
      return a.url !== b.url;
    case 'equals|starts with':
      return !a.url.startsWith(b.url);
    case 'starts with|equals':
      return !b.url.startsWith(a.url);
    case 'equals|ends with':
      return !a.url.endsWith(b.url);
    case 'ends with|equals':
      return !b.url.endsWith(a.url);
    case 'equals|contains':
      return !a.url.includes(b.url);
    case 'contains|equals':
      return !b.url.includes(a.url);
    // Two prefixes describe disjoint sets unless one is a prefix of the other, in
    // which case the narrower set sits entirely inside the wider one.
    case 'starts with|starts with':
      return !a.url.startsWith(b.url) && !b.url.startsWith(a.url);
    case 'ends with|ends with':
      return !a.url.endsWith(b.url) && !b.url.endsWith(a.url);
    default:
      return false;
  }
};

export const urlsOverlap = (a: Experiment, b: Experiment): boolean => {
  const rulesA = urlRulesOf(a);
  const rulesB = urlRulesOf(b);

  // No rules means every URL, which overlaps anything.
  if (rulesA.length === 0 || rulesB.length === 0) return true;

  return !rulesA.every((ruleA) => rulesB.every((ruleB) => rulesProvablyDisjoint(ruleA, ruleB)));
};

export type IncompatibleReason = 'dates' | 'devices';

/**
 * Why a candidate cannot join the current selection, or null if it can.
 * A candidate must overlap with every already-selected experiment, since the
 * combination is only defined over traffic common to all of them.
 */
export const incompatibleReason = (candidate: Experiment, selected: Experiment[]): IncompatibleReason | null => {
  for (const other of selected) {
    if (other.id === candidate.id) continue;
    if (!datesOverlap(candidate, other)) return 'dates';
    if (!devicesOverlap(candidate, other)) return 'devices';
  }
  return null;
};

/** The window during which every selected experiment was live. */
export const overlapWindow = (experiments: Experiment[]): { start: number; end: number | null } | null => {
  if (experiments.length === 0) return null;
  const start = Math.max(...experiments.map(startOf));
  const end = Math.min(...experiments.map(endOf));
  if (!Number.isFinite(start) || start > end) return null;
  return { start, end: Number.isFinite(end) ? end : null };
};

/** Devices every selected experiment targeted. */
export const sharedDevices = (experiments: Experiment[]): DeviceType[] => {
  if (experiments.length === 0) return [];
  return experiments
    .map(devicesOf)
    .reduce((acc, devices) => acc.filter((d) => devices.includes(d)), [...CONCRETE_DEVICES]);
};

/**
 * Span used to lay out the timeline bars. Running experiments are clamped to
 * "now" so an open-ended bar does not stretch the axis to infinity.
 */
export const timelineSpan = (experiments: Experiment[], now: number): { min: number; max: number } => {
  const starts = experiments.map(startOf).filter(Number.isFinite);
  const ends = experiments.map((e) => e.endsAt ?? now).filter(Number.isFinite);
  const min = starts.length ? Math.min(...starts) : now;
  const max = ends.length ? Math.max(...ends, min + 1) : now;
  return { min, max: Math.max(max, min + 1) };
};

/** Position of an experiment's run as percentages across the shared span. */
export const barGeometry = (experiment: Experiment, span: { min: number; max: number }, now: number) => {
  const total = span.max - span.min || 1;
  const start = Math.max(startOf(experiment), span.min);
  const end = Math.min(experiment.endsAt ?? now, span.max);
  const left = ((start - span.min) / total) * 100;
  const width = Math.max(((end - start) / total) * 100, 1.5);
  return { left: Math.max(0, Math.min(left, 100)), width: Math.min(width, 100 - Math.max(0, left)) };
};
